import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { clamp, roundTo } from '../types/geometry.js';
import { meanLumaInRect } from '../image/analysis.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Lighting-uniformity check.
 *
 * A face lit from one side produces a hard shadow that consulates reject, even
 * when the average exposure looks fine. The check splits the detected face box
 * into left/right halves, compares their mean brightness, and — when pixels are
 * available — also flags a backlit frame (background much brighter than face).
 *
 * The rectangles it returns are drawn on the photo, so the user sees exactly
 * which side of the face is too dark.
 */
export const lightingCheck: ValidationCheck = {
  id: 'lighting',
  title: 'Освещение',
  description: 'Сравнивает яркость левой и правой половины лица, ищет тени и контровой свет.',
  kind: 'quality',
  requires: ['pixels', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'geometry']);
    if (missing) return notChecked(ctx, lightingCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    const pixels = ctx.pixels;
    if (!geometry || !pixels) {
      return notChecked(ctx, lightingCheck, 'Недостаточно данных для анализа освещения лица.', ctx.rule.severity);
    }

    const maxSideDifference = numberParam(ctx.rule.params, 'maxSideDifference', 38);
    const maxBacklightDifference = numberParam(ctx.rule.params, 'maxBacklightDifference', 110);

    /**
     * Measure the inner part of the face box, not its corners: a bounding box
     * of a face always contains background pixels, and background is much
     * brighter than skin, which used to look like backlighting.
     */
    const face = geometry.faceBox;
    const inner = {
      x: face.x + face.width * 0.15,
      y: face.y + face.height * 0.25,
      width: face.width * 0.7,
      height: face.height * 0.5,
    };
    const midX = inner.x + inner.width / 2;
    const halfWidth = inner.width / 2;

    const leftRect = {
      x: clamp(inner.x, 0, 1),
      y: clamp(inner.y, 0, 1),
      width: clamp(halfWidth, 0, 1),
      height: clamp(inner.height, 0, 1),
    };
    const rightRect = {
      x: clamp(midX, 0, 1),
      y: clamp(inner.y, 0, 1),
      width: clamp(halfWidth, 0, 1),
      height: clamp(inner.height, 0, 1),
    };

    const { luma, width, height } = pixels;
    const leftMean = meanLumaInRect(luma, width, height, leftRect);
    const rightMean = meanLumaInRect(luma, width, height, rightRect);
    const faceMean = (leftMean + rightMean) / 2;
    const sideDifference = Math.abs(leftMean - rightMean);

    // Background patches beside the face, to detect backlighting.
    const backgroundSample = {
      x: clamp(Math.max(0, face.x - 0.1), 0, 1),
      y: clamp(inner.y, 0, 1),
      width: clamp(0.05, 0, 1),
      height: clamp(inner.height, 0, 1),
    };
    const backgroundMean = meanLumaInRect(luma, width, height, backgroundSample);
    const backlightDifference = backgroundMean - faceMean;

    const darkerSide = leftMean < rightMean ? 'left' : 'right';
    const severities: Array<'pass' | 'warning' | 'fail'> = [];
    severities.push(
      sideDifference > maxSideDifference ? 'fail' : sideDifference > maxSideDifference * 0.6 ? 'warning' : 'pass',
    );
    severities.push(
      backlightDifference > maxBacklightDifference
        ? 'fail'
        : backlightDifference > maxBacklightDifference * 0.6
          ? 'warning'
          : 'pass',
    );
    const status = severities.includes('fail') ? 'fail' : severities.includes('warning') ? 'warning' : 'pass';

    const problems: string[] = [];
    if (sideDifference > maxSideDifference * 0.6) {
      problems.push(
        `лицо освещено неравномерно: разница между половинами ${roundTo(sideDifference, 0)} (${darkerSide === 'left' ? 'левая' : 'правая'} сторона темнее)`,
      );
    }
    if (backlightDifference > maxBacklightDifference * 0.6) {
      problems.push('источник света находится позади — лицо темнее фона');
    }

    const darkerRect = darkerSide === 'left' ? leftRect : rightRect;

    return plainResult({
      id: lightingCheck.id,
      title: lightingCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(sideDifference, 1),
      max: maxSideDifference,
      message:
        status === 'pass'
          ? 'Лицо освещено равномерно, жёстких теней нет.'
          : `Освещение лица: ${problems.join('; ')}.`,
      requirement: 'Лицо освещено равномерно, без жёстких теней и контрового света.',
      fixability: 'retake',
      geometry: [
        {
          type: 'rect',
          coordinates: [roundTo(leftRect.x, 4), roundTo(leftRect.y, 4), roundTo(leftRect.width, 4), roundTo(leftRect.height, 4)],
        },
        {
          type: 'rect',
          coordinates: [roundTo(rightRect.x, 4), roundTo(rightRect.y, 4), roundTo(rightRect.width, 4), roundTo(rightRect.height, 4)],
        },
      ],
      problemRegions:
        status === 'pass'
          ? []
          : [
              {
                id: 'lighting_dark_side',
                rect: {
                  x: roundTo(darkerRect.x, 4),
                  y: roundTo(darkerRect.y, 4),
                  width: roundTo(darkerRect.width, 4),
                  height: roundTo(darkerRect.height, 4),
                },
                message:
                  backlightDifference > maxBacklightDifference * 0.6
                    ? 'Лицо темнее фона: свет идёт сзади.'
                    : 'Эта сторона лица в тени.',
                severity: status === 'fail' ? 'error' : 'warning',
              },
            ],
      confidence: 0.7,
      details: {
        leftMean: roundTo(leftMean, 1),
        rightMean: roundTo(rightMean, 1),
        sideDifference: roundTo(sideDifference, 1),
        backgroundMean: roundTo(backgroundMean, 1),
        backlightDifference: roundTo(backlightDifference, 1),
        limits: { maxSideDifference, maxBacklightDifference },
        displayValue: `${roundTo(sideDifference, 0).toFixed(0)}`,
      },
    });
  },
};
